import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { useAuth } from '../lib/auth-context';
import { searchApi } from '../lib/api';
import { Search, Loader2, ChevronRight, X } from 'lucide-react';

/**
 * Global search box for the top bar — present on every admin page.
 * Debounced live results in a floating dropdown, grouped by entity type.
 */
export default function GlobalSearch() {
  const { currentProperty } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const run = useCallback(async (term) => {
    if (!currentProperty?.id || term.trim().length < 2) { setData(null); return; }
    try {
      setLoading(true);
      const r = await searchApi.query(currentProperty.id, term.trim());
      setData(r.data);
    } catch (e) {
      setData({ groups: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [currentProperty]);

  // debounce
  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return; }
    const t = setTimeout(() => run(q), 300);
    return () => clearTimeout(t);
  }, [q, run]);

  // close on outside click
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (href) => { setOpen(false); setQ(''); setData(null); navigate(href); };

  return (
    <div ref={boxRef} className="relative hidden md:block w-72">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        placeholder="Search everything…"
        className="pl-9 pr-8 h-9"
        data-testid="global-search-input"
      />
      {q && (
        <button onClick={() => { setQ(''); setData(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      )}

      {open && q.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-[420px] max-w-[90vw] right-0 md:left-0 rounded-lg border bg-card shadow-lg max-h-[70vh] overflow-y-auto">
          {loading && <div className="p-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>}
          {!loading && data && data.total === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">No results for “{q}”.</div>
          )}
          {!loading && data && data.groups.map((grp) => (
            <div key={grp.type}>
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">{grp.type}</p>
              {grp.results.map((r) => (
                <button key={r.id} onClick={() => go(r.href)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted transition">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
